import { useState, useEffect } from "react";

interface NativelyEnvironment {
  isNativelyApp: boolean;
  isReady: boolean;
  platform: "ios" | "android" | null;
  appVersion: string | null;
}

// Note: Window types for Natively SDK are handled by the 'natively' package

export function useNativelyEnvironment(): NativelyEnvironment {
  const [state, setState] = useState<NativelyEnvironment>({
    isNativelyApp: false,
    isReady: false,
    platform: null,
    appVersion: null,
  });

  useEffect(() => {
    const checkNatively = async () => {
      try {
        // Check for Natively SDK presence (Window extensions declared in @/types/domain)
        const natively = window.natively;
        const nativelyInfo = window.NativelyInfo;

        if (nativelyInfo && typeof nativelyInfo.isNativeApp === "function") {
          // Use NativelyInfo API
          const isNative = nativelyInfo.isNativeApp();
          if (isNative) {
            const platformStr = nativelyInfo.getPlatform?.()?.toLowerCase() || "";
            const platform = platformStr.includes("ios")
              ? "ios"
              : platformStr.includes("android")
                ? "android"
                : null;

            setState({
              isNativelyApp: true,
              isReady: true,
              platform,
              appVersion: nativelyInfo.getAppVersion?.() || null,
            });
            return;
          }
        }

        if (natively?.isNativeApp) {
          // Use window.natively API
          const deviceInfo = await natively.getDeviceInfo?.();
          const platformStr = deviceInfo?.platform?.toLowerCase() || "";
          const platform = platformStr.includes("ios")
            ? "ios"
            : platformStr.includes("android")
              ? "android"
              : null;

          setState({
            isNativelyApp: true,
            isReady: true,
            platform,
            appVersion: deviceInfo?.appVersion || null,
          });
          return;
        }

        // Check for native user agent hints
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes("natively")) {
          const platform = userAgent.includes("ios")
            ? "ios"
            : userAgent.includes("android")
              ? "android"
              : null;
          setState({
            isNativelyApp: true,
            isReady: true,
            platform,
            appVersion: null,
          });
          return;
        }

        // Not running in Natively
        setState({
          isNativelyApp: false,
          isReady: true,
          platform: null,
          appVersion: null,
        });
      } catch (error) {
        console.error(error);
        setState({
          isNativelyApp: false,
          isReady: true,
          platform: null,
          appVersion: null,
        });
      }
    };

    // Small delay to allow Natively SDK to initialize
    const timeout = setTimeout(checkNatively, 100);
    return () => clearTimeout(timeout);
  }, []);

  return state;
}

// Utility function for one-time check (useful outside React components)
export function isNativelyApp(): boolean {
  const natively = window.natively;
  const nativelyInfo = window.NativelyInfo;
  const Natively = window.Natively;
  const userAgent = navigator.userAgent.toLowerCase();

  // Method 1: NativelyInfo API (most common)
  if (nativelyInfo && typeof nativelyInfo.isNativeApp === "function") {
    return nativelyInfo.isNativeApp();
  }

  // Method 2: window.natively object
  if (natively?.isNativeApp) {
    return true;
  }

  // Method 3: window.Natively object (capital N)
  if (Natively?.isNativeApp || Natively?.isNative) {
    return true;
  }

  // Method 4: User agent check
  if (userAgent.includes("natively") || userAgent.includes("buildnatively")) {
    return true;
  }

  // Method 5: Check for data-natively attribute
  const hasNativelyMarker = document.documentElement.hasAttribute("data-natively");

  if (hasNativelyMarker) {
    return true;
  }

  return false;
}
